import { useState } from 'react';
import { Input, Button } from '@/components/ui';
import { AddressFields } from './AddressFields';
import { CustomerSelector } from './CustomerSelector';
import { PhoneInputList } from '@/features/customers/components';
import type { ProjectCustomerFormItem, CustomerListItem, PhoneFormItem } from '../types';

interface ProjectCustomerItemProps {
  customer: ProjectCustomerFormItem;
  index: number;
  customers: CustomerListItem[];
  onChange: (tempId: string, updates: Partial<ProjectCustomerFormItem>) => void;
  onRemove: (tempId: string) => void;
  onSetPrimary: (tempId: string) => void;
  onCustomerSearch: (term: string) => void;
  onClearCustomerSearch: () => void;
  isLoadingCustomers: boolean;
  isSearchingCustomers: boolean;
  canRemove: boolean;
  disabled?: boolean;
  errors?: Record<string, string>;
}

export function ProjectCustomerItem({
  customer,
  index,
  customers,
  onChange,
  onRemove,
  onSetPrimary,
  onCustomerSearch,
  onClearCustomerSearch,
  isLoadingCustomers,
  isSearchingCustomers,
  canRemove,
  disabled = false,
  errors = {},
}: ProjectCustomerItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isNewCustomer = customer.mode === 'new';

  const handleChange = (field: string, value: string | boolean) => {
    onChange(customer.tempId, { [field]: value } as Partial<ProjectCustomerFormItem>);
  };

  const handlePhonesChange = (phones: PhoneFormItem[]) => {
    onChange(customer.tempId, { phones });
  };

  const handleSelectExisting = (customerId: string) => {
    if (!customerId) {
      // Clearing selection
      onChange(customer.tempId, {
        existingCustomerId: '',
        existingCustomerName: '',
      });
      return;
    }

    const selectedCustomer = customers.find((c) => c.id === customerId);
    // Always provide a fallback name - use full_name, or construct from first/last, or use a placeholder
    const customerName = selectedCustomer?.full_name
      || (selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim() : '')
      || 'Selected Customer';

    onChange(customer.tempId, {
      existingCustomerId: customerId,
      existingCustomerName: customerName,
    });
  };

  // Get display name for collapsed view
  const getDisplayName = () => {
    if (customer.mode === 'existing' && customer.existingCustomerName) {
      return customer.existingCustomerName;
    }
    if (customer.mode === 'new' && (customer.firstName || customer.lastName)) {
      return `${customer.firstName} ${customer.lastName}`.trim();
    }
    return 'New Customer';
  };

  // Error key prefix for this customer
  const errorPrefix = `customer_${index}_`;

  return (
    <div className="bg-white rounded-lg border border-secondary-200">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-secondary-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-secondary-400"
          >
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div>
            <span className="font-medium text-secondary-900">{getDisplayName()}</span>
            <div className="flex items-center gap-2 mt-0.5">
              {customer.isPrimary && (
                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  Primary
                </span>
              )}
              {customer.isBilling && (
                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                  Billing
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {!customer.isPrimary && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSetPrimary(customer.tempId)}
              disabled={disabled}
            >
              Set Primary
            </Button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={() => onRemove(customer.tempId)}
              disabled={disabled}
              className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Remove customer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Customer Mode Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleChange('mode', 'new')}
              disabled={disabled}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors
                         ${isNewCustomer
                           ? 'bg-primary-600 text-white'
                           : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                         }
                         ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              New Customer
            </button>
            <button
              type="button"
              onClick={() => handleChange('mode', 'existing')}
              disabled={disabled}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors
                         ${!isNewCustomer
                           ? 'bg-primary-600 text-white'
                           : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                         }
                         ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Existing Customer
            </button>
          </div>

          {isNewCustomer ? (
            /* New Customer Form */
            <div className="space-y-4">
              {/* First Name and Last Name Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name={`${errorPrefix}firstName`}
                  value={customer.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="John"
                  error={errors[`${errorPrefix}firstName`]}
                  disabled={disabled}
                  required
                />
                <Input
                  label="Last Name"
                  name={`${errorPrefix}lastName`}
                  value={customer.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Smith"
                  error={errors[`${errorPrefix}lastName`]}
                  disabled={disabled}
                  required
                />
              </div>

              {/* Email */}
              <Input
                label="Email"
                name={`${errorPrefix}email`}
                type="email"
                value={customer.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
                error={errors[`${errorPrefix}email`]}
                disabled={disabled}
                required
              />

              {/* Phone Numbers */}
              <div className="pt-4 border-t border-secondary-200">
                <h4 className="text-base font-semibold text-secondary-900 mb-4">
                  Phone Numbers
                </h4>
                <PhoneInputList
                  phones={customer.phones}
                  onChange={handlePhonesChange}
                  maxPhones={3}
                  disabled={disabled}
                />
              </div>

              {/* Billing Address Section */}
              <div className="pt-4 border-t border-secondary-200">
                <h4 className="text-base font-semibold text-secondary-900 mb-4">
                  Billing Address
                </h4>
                <AddressFields
                  streetAddress={customer.billingStreetAddress}
                  unit={customer.billingUnit}
                  city={customer.billingCity}
                  province={customer.billingProvince}
                  postalCode={customer.billingPostalCode}
                  country={customer.billingCountry}
                  onChange={(field, value) => handleChange(field, value)}
                  errors={{
                    streetAddress: errors[`${errorPrefix}billingStreetAddress`],
                    city: errors[`${errorPrefix}billingCity`],
                    province: errors[`${errorPrefix}billingProvince`],
                    postalCode: errors[`${errorPrefix}billingPostalCode`],
                    country: errors[`${errorPrefix}billingCountry`],
                  }}
                  disabled={disabled}
                />
              </div>
            </div>
          ) : (
            /* Existing Customer Selector */
            <div>
              <CustomerSelector
                customers={customers}
                selectedCustomerId={customer.existingCustomerId || ''}
                selectedCustomerName={customer.existingCustomerName}
                onSelect={handleSelectExisting}
                onSearch={onCustomerSearch}
                onClearSearch={onClearCustomerSearch}
                isLoading={isLoadingCustomers}
                isSearching={isSearchingCustomers}
                disabled={disabled}
                error={errors[`${errorPrefix}existingCustomerId`]}
              />
            </div>
          )}

          {/* Billing Contact Checkbox */}
          <div className="pt-4 border-t border-secondary-200">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={customer.isBilling}
                onChange={(e) => handleChange('isBilling', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                disabled={disabled}
              />
              <span className="text-sm font-medium text-secondary-700">
                Set as billing contact
              </span>
            </label>
            <p className="text-xs text-secondary-500 mt-1 ml-6">
              Invoices will be sent to this customer
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
